import './docButtonRow.scss';
import {
    useSearchPageType,
    useSearchURL,
    useSelectedDocContext,
    useStateCurrentPageNumber,
    useStateResultDetailsTab
} from '../../../search-hooks';
import React, {useCallback, useEffect, useState} from 'react';
import {Icons} from '../../../../icons/icons';
import {extractDownloadLink} from '../../../search.utils';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {HibuOverlay} from '../../../../common/hibuOverlay/hibuOverlay';
import {HibuLink} from '../../../../common/hibuButton/hibuLink';
import {URL_FILTER_PARAM, URL_PAGE_PARAM} from '../../../const';
import {HibuTag} from '../../../search-types';
import {Dropdown} from 'react-bootstrap';
import {SvgIcon} from '../../../../icons/svgIcon';
import {encodeTab} from '../../../url/tab-type-parse';

type DocButtonRowProps = {
    hasSearchQuery?: boolean
}
export const DocButtonRow = ({hasSearchQuery}: DocButtonRowProps): JSX.Element | null => {
    const {t} = useTranslation();
    const searchPageType = useSearchPageType();
    const doc = useSelectedDocContext();
    const pageNumber = useStateCurrentPageNumber();
    const searchURL = useSearchURL();
    const showTechnicalDetails = useSelector((state) => state.settings.technicalDetails);
    const {
        showDownloadButton,
        showLinkToClipboardButton,
        showLinkByMailButton
    } = useSelector((state) => state.config.search.documentActions.toggles);

    const [documentLink, setDocumentLink] = useState('');
    const [mailSubject, setMailSubject] = useState('');
    const [mailBody, setMailBody] = useState('');
    const linkToClipboardKey = 'search.label.detailButtons.tooltips.linkToClipboard';
    const contentToClipboardKey = 'search.label.detailButtons.tooltips.copyContent';
    const contentUriToClipboardKey = 'search.label.detailButtons.tooltips.contentUriToClipboard';
    const copiedKey = 'search.label.detailButtons.tooltips.copied';
    const [linkToClipboardComputedKey, setLinkToClipboardComputedKey] = useState(linkToClipboardKey);
    const [contentToClipboardComputedKey, setContentToClipboardComputedKey] = useState(contentToClipboardKey);
    const [contentUriToClipboardComputedKey, setContentUriToClipboardComputedKey] = useState(contentUriToClipboardKey);
    const [contentUri, setContentUri] = useState<string | undefined>(undefined);
    const openTab = useStateResultDetailsTab();
    const updateDocumentDetails = useCallback(doc => {
        const filter = `?${URL_FILTER_PARAM}=id[${doc?.id}]&${URL_PAGE_PARAM}=${pageNumber}&${encodeTab(openTab)}`;
        setDocumentLink(encodeURI(`${searchURL}${filter}`));
        setMailSubject(t('search.share.link.mailSubject', {documentTitle: doc.title}));
        setMailBody(t('search.share.link.mailBody', {documentTitle: doc.title}) + documentLink);
    }, [documentLink, pageNumber, searchURL, t, openTab]);

    useEffect(() => doc && updateDocumentDetails(doc), [doc, updateDocumentDetails]);

    const copyTextToClipboard = async (val: string): Promise<void> => {
        return await navigator.clipboard.writeText(val);
    };

    const sendLinkByMail = useCallback(() => {
        window.location.href = `mailto:?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;
    }, [mailBody, mailSubject]);

    useEffect(() => {
        let contentUri = doc?.metadata.contentUri_string;
        if (!contentUri) {
            const contentUriTags: HibuTag[] | undefined = doc?.hibuTags['contentUri'];
            if (contentUriTags) {
                contentUri = doc?.hibuTags['contentUri'][0].extra?.value;
            }
        }
        setContentUri(contentUri);
    }, [contentUri, doc?.hibuTags, doc?.metadata.contentUri_string]);

    if (!doc) return null;

    const buttons = () => {
        return (
            <div className={`preview-header-group document-buttons ${hasSearchQuery && 'adapt_small_screen'}`}>
                {showLinkToClipboardButton &&
                    <HibuOverlay
                        className={'document-button'}
                        onClick={() => {
                            copyTextToClipboard(documentLink).then(() => setLinkToClipboardComputedKey(copiedKey));
                        }}
                        resetFunction={() =>
                            setLinkToClipboardComputedKey(linkToClipboardKey)}
                        icon={Icons.CLOUD_LINK}
                        tooltipKey={linkToClipboardComputedKey}
                        size={'md'}
                    />
                }
                {showLinkByMailButton &&
                    <HibuOverlay
                        className={'document-button'}
                        onClick={sendLinkByMail}
                        icon={Icons.MAIL}
                        tooltipKey={'search.label.detailButtons.tooltips.linkToMail'}
                        size={'md'}
                    />
                }
                {showDownloadButton && doc.metadata.downloadLink_string && (
                    <HibuLink
                        className={'document-button'}
                        href={extractDownloadLink(searchPageType, doc, true)}
                        icon={Icons.DOWNLOAD}
                        tooltipKey={'search.label.detailButtons.tooltips.downloadFile'}
                        visible
                        size={'md'}
                    />
                )}
                {showTechnicalDetails && doc?.content && (
                    <HibuOverlay
                        className={'document-button'}
                        onClick={() => {
                            copyTextToClipboard(doc?.content ? doc.content : '').then(() => setContentToClipboardComputedKey(copiedKey));
                        }}
                        icon={Icons.CLIPBOARD}
                        resetFunction={() => setContentToClipboardComputedKey(contentToClipboardKey)}
                        tooltipKey={contentToClipboardComputedKey}
                        size={'md'}
                    />
                )}
                {contentUri && (
                    <>
                        {
                            contentUri.startsWith('http') ? (
                                    <HibuLink
                                        className={'document-button'}
                                        href={doc.metadata.contentUri_string}
                                        icon={Icons.OPEN_IN}
                                        tooltipKey={'search.label.detailButtons.tooltips.cloudLink'}
                                        visible
                                        size={'md'}
                                    />
                                ) :
                                (
                                    <HibuOverlay
                                        className={'document-button'}
                                        onClick={() => {
                                            copyTextToClipboard(contentUri).then(() => setContentUriToClipboardComputedKey(copiedKey));
                                        }}
                                        resetFunction={() =>
                                            setContentUriToClipboardComputedKey(contentUriToClipboardKey)}
                                        icon={Icons.OPEN_IN}
                                        tooltipKey={contentUriToClipboardComputedKey}
                                        size={'md'}
                                    />
                                )
                        }
                    </>
                )}
                <></>
            </div>
        );
    };


    return (
        <>
            <Dropdown id={'doc-buttons-vertical'}
                      className={`small_screen_only ${hasSearchQuery && 'adapt_small_screen'}`}>
                <Dropdown.Toggle id="doc-button-toggle">
                    <SvgIcon icon={Icons.MENU} id={'icon-header-menu'}/>
                </Dropdown.Toggle>
                <Dropdown.Menu id={'doc-button-menu'}>
                    {buttons()}
                </Dropdown.Menu>
            </Dropdown>
            <div className={`big_screen_only ${hasSearchQuery ? 'disabled_small_screen' : ''}`}>
                {buttons()}
            </div>
        </>);

};
